import { useState, useRef, Fragment, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  supplierCatalogsApi,
  Supplier,
  SupplierUpdate,
  ImportPreview,
  ImportResult,
  ReferenceMapping,
  SupplierProductForMapping,
} from '@/lib/api'
import { formatPrice } from '@/lib/utils'
import {
  Plus,
  Upload,
  Link2,
  RefreshCw,
  Search,
  Trash2,
  Edit3,
  ChevronDown,
  ChevronRight,
  Package,
  Store,
  CheckCircle,
  XCircle,
  TrendingUp,
  X,
  Link,
  Unlink,
  Sparkles,
  CheckSquare,
  Square,
  AlertTriangle,
  Loader2,
} from 'lucide-react'
import { useToast } from '@/components/ui/Toast'
import EmptyState from '@/components/ui/EmptyState'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useFormValidation, ValidationSchema } from '@/hooks/useFormValidation'
import BatchCreateModal from '@/components/BatchCreateModal'

// Tamaño de página para listado de productos
const PRODUCTS_PAGE_SIZE = 1000

// Mapeo de campos disponibles
const FIELD_MAPPINGS = [
  { key: 'reference', label: 'Referencia', required: true },
  { key: 'name', label: 'Nombre', required: true },
  { key: 'cost_price', label: 'Precio Coste', required: false },
  { key: 'rrp_price', label: 'PVP Recomendado', required: false },
  { key: 'stock', label: 'Stock', required: false },
  { key: 'ean', label: 'EAN/Codigo Barras', required: false },
  { key: 'brand', label: 'Marca', required: false },
  { key: 'category', label: 'Categoria', required: false },
  { key: 'description', label: 'Descripcion', required: false },
]

export default function SupplierCatalogsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // Estado general
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [productSearch, setProductSearch] = useState('')
  const [productExclude, setProductExclude] = useState('')
  const [productPage, setProductPage] = useState(1)
  const [filterReference, setFilterReference] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [hideMapped, setHideMapped] = useState(false)
  const [expandedProduct, setExpandedProduct] = useState<number | null>(null)
  const [sortBy, setSortBy] = useState<string>('stock')
  const [sortOrder, setSortOrder] = useState<string>('desc')

  // Estado de selección múltiple para creación batch
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set())
  const [showBatchModal, setShowBatchModal] = useState(false)

  // Estado de importacion
  const [importUrl, setImportUrl] = useState('')
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importLocalPath, setImportLocalPath] = useState('')
  const [importSource, setImportSource] = useState<'url' | 'file' | 'local'>('url')
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null)
  const [columnMapping, setColumnMapping] = useState<Record<string, number>>({})
  const [importStep, setImportStep] = useState<'select' | 'preview' | 'mapping' | 'result'>('select')
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [isImporting, setIsImporting] = useState(false)

  // Estado de creacion de proveedor
  const [supplierForm, setSupplierForm] = useState({
    code: '',
    name: '',
    email: '',
    phone: '',
    notes: '',
    csv_delimiter: 'auto',
    csv_encoding: 'auto',
    csv_has_header: true,
    catalog_url: '',
    import_frequency_days: 0,
  })

  // Estado de edicion de proveedor
  const [editForm, setEditForm] = useState({
    code: '',
    name: '',
    email: '',
    phone: '',
    notes: '',
    csv_delimiter: 'auto',
    csv_encoding: 'auto',
    csv_has_header: true,
    catalog_url: '',
    import_frequency_days: 0,
  })

  // Validación del formulario de proveedor
  type SupplierFormValues = {
    code: string
    name: string
    catalog_url: string
  }

  const supplierValidationSchema = useMemo<ValidationSchema<SupplierFormValues>>(() => ({
    code: {
      required: 'El código es obligatorio',
      minLength: { value: 1, message: 'El código es obligatorio' },
      maxLength: { value: 10, message: 'Máximo 10 caracteres' },
      pattern: { value: /^[A-Za-z0-9_-]+$/, message: 'Solo letras, números, guiones y guiones bajos' }
    },
    name: {
      required: 'El nombre es obligatorio',
      minLength: { value: 2, message: 'Mínimo 2 caracteres' },
      maxLength: { value: 100, message: 'Máximo 100 caracteres' }
    },
    catalog_url: {
      custom: [{
        validate: (value: string) => {
          if (!value) return true // Opcional
          // Aceptar URLs (http/https) o rutas locales (empiezan con /)
          const isUrl = /^https?:\/\//.test(value)
          const isLocalPath = value.startsWith('/')
          return isUrl || isLocalPath
        },
        message: 'Debe ser una URL (http://) o una ruta local (/ruta/archivo)'
      }]
    }
  }), [])

  const supplierFormValues: SupplierFormValues = {
    code: supplierForm.code,
    name: supplierForm.name,
    catalog_url: supplierForm.catalog_url
  }

  const {
    visibleErrors: supplierErrors,
    touchAll: touchAllSupplierFields,
    isValid: isSupplierFormValid,
    getFieldProps: getSupplierFieldProps,
    resetTouched: resetSupplierTouched
  } = useFormValidation(supplierFormValues, supplierValidationSchema)

  // Estado de mapeos de referencias
  const [showMappingModal, setShowMappingModal] = useState(false)
  const [mappingSearch, setMappingSearch] = useState('')
  const [mappingProductSearch, setMappingProductSearch] = useState('')
  const [selectedFactusolRef, setSelectedFactusolRef] = useState<string | null>(null)

  // Estado para popup de confirmación de despublicación
  const [unpublishConfirm, setUnpublishConfirm] = useState<{
    show: boolean
    productId: number | null
    productName: string
    target: 'prestashop' | 'factusol' | null
  }>({ show: false, productId: null, productName: '', target: null })
  const [mappingPage, setMappingPage] = useState(1)

  // Queries
  const suppliersQuery = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => supplierCatalogsApi.list(),
  })

  const globalStatsQuery = useQuery({
    queryKey: ['supplier-global-stats'],
    queryFn: () => supplierCatalogsApi.getGlobalStats(),
  })

  const supplierStatsQuery = useQuery({
    queryKey: ['supplier-stats', selectedSupplier?.id],
    queryFn: () => supplierCatalogsApi.getStats(selectedSupplier!.id),
    enabled: !!selectedSupplier,
  })

  const productsQuery = useQuery({
    queryKey: ['supplier-products', selectedSupplier?.id, productPage, productSearch, productExclude, filterReference, filterCategory, hideMapped, sortBy, sortOrder],
    queryFn: () =>
      supplierCatalogsApi.listProducts(selectedSupplier!.id, {
        page: productPage,
        page_size: PRODUCTS_PAGE_SIZE,
        search: productSearch || undefined,
        exclude: productExclude || undefined,
        reference_filter: filterReference || undefined,
        category_filter: filterCategory || undefined,
        hide_mapped: hideMapped || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      }),
    enabled: !!selectedSupplier,
  })

  const priceHistoryQuery = useQuery({
    queryKey: ['price-history', selectedSupplier?.id, expandedProduct],
    queryFn: () =>
      supplierCatalogsApi.getPriceHistory(selectedSupplier!.id, expandedProduct!, 30),
    enabled: !!selectedSupplier && !!expandedProduct,
  })

  // Query para obtener mapeos del proveedor
  const supplierMappingsQuery = useQuery({
    queryKey: ['supplier-mappings', selectedSupplier?.id],
    queryFn: () => supplierCatalogsApi.getSupplierMappings(selectedSupplier!.id),
    enabled: !!selectedSupplier && showMappingModal,
  })

  // Query para obtener productos de Factusol
  const factusolProductsQuery = useQuery({
    queryKey: ['factusol-products', mappingSearch, mappingPage],
    queryFn: async () => {
      const response = await fetch(`/api/factusol/products?search=${encodeURIComponent(mappingSearch)}&page=${mappingPage}&limit=20`)
      return response.json()
    },
    enabled: showMappingModal,
  })

  // Query para obtener productos del proveedor para mapear
  const supplierProductsForMappingQuery = useQuery({
    queryKey: ['supplier-products-mapping', selectedSupplier?.id, mappingProductSearch],
    queryFn: async () => {
      console.log('Fetching supplier products for mapping:', {
        supplierId: selectedSupplier?.id,
        search: mappingProductSearch
      })
      const result = await supplierCatalogsApi.getProductsForMapping(
        selectedSupplier!.id,
        mappingProductSearch,
        false,
        50
      )
      console.log('Supplier products for mapping result:', result)
      return result
    },
    enabled: !!selectedSupplier && showMappingModal && !!selectedFactusolRef,
  })

  // Mutations
  const createSupplierMutation = useMutation({
    mutationFn: (data: typeof supplierForm) => supplierCatalogsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      setShowCreateModal(false)
      resetSupplierForm()
      toast.success('Proveedor creado correctamente')
    },
    onError: (error: any) => {
      toast.error('Error al crear proveedor', error.response?.data?.detail || error.message)
    },
  })

  const updateSupplierMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: SupplierUpdate }) =>
      supplierCatalogsApi.update(id, data),
    onSuccess: async (updatedSupplier) => {
      // Refrescar la lista de proveedores
      await queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      // Actualizar el proveedor seleccionado directamente con los datos del servidor
      setSelectedSupplier(updatedSupplier)
      setShowEditModal(false)
      setEditingSupplier(null)
      toast.success('Proveedor actualizado')
    },
    onError: (error: any) => {
      toast.error('Error al actualizar proveedor', error.response?.data?.detail || error.message)
    },
  })

  const deleteSupplierMutation = useMutation({
    mutationFn: (id: number) => supplierCatalogsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      if (selectedSupplier) {
        setSelectedSupplier(null)
        setSelectedProductIds(new Set()) // Limpiar selección al eliminar proveedor
      }
      toast.success('Proveedor eliminado')
    },
    onError: (error: any) => {
      toast.error('Error al eliminar proveedor', error.response?.data?.detail || error.message)
    },
  })

  const publishMutation = useMutation({
    mutationFn: async ({ supplierId, productId, target }: { supplierId: number; productId: number; target: 'prestashop' | 'factusol' | 'all' }) => {
      if (target === 'prestashop') return supplierCatalogsApi.publishToPrestaShop(supplierId, productId)
      if (target === 'factusol') return supplierCatalogsApi.publishToFactusol(supplierId, productId)
      return supplierCatalogsApi.publishToAll(supplierId, productId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-products'] })
      queryClient.invalidateQueries({ queryKey: ['supplier-stats'] })
      toast.success('Producto publicado')
    },
    onError: (error: any) => {
      toast.error('Error al publicar producto', error.response?.data?.detail || error.message)
    },
  })

  // Mutation para despublicar (marcar como no publicado en BD local)
  const unpublishMutation = useMutation({
    mutationFn: async ({ supplierId, productId, target }: { supplierId: number; productId: number; target: 'prestashop' | 'factusol' }) => {
      if (target === 'prestashop') return supplierCatalogsApi.unpublishFromPrestaShop(supplierId, productId)
      return supplierCatalogsApi.unpublishFromFactusol(supplierId, productId)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['supplier-products'] })
      queryClient.invalidateQueries({ queryKey: ['supplier-stats'] })
      const targetName = variables.target === 'prestashop' ? 'PrestaShop' : 'Factusol'
      toast.success(`Producto desmarcado de ${targetName}`)
      setUnpublishConfirm({ show: false, productId: null, productName: '', target: null })
    },
    onError: (error: any) => {
      toast.error('Error al desmarcar producto', error.response?.data?.detail || error.message)
    },
  })

  // Mutation para crear mapeo
  const createMappingMutation = useMutation({
    mutationFn: (data: { factusol_reference: string; supplier_id: number; supplier_product_id: number; notes?: string }) =>
      supplierCatalogsApi.createMapping(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-mappings'] })
      queryClient.invalidateQueries({ queryKey: ['supplier-products-mapping'] })
      setSelectedFactusolRef(null)
      setMappingProductSearch('')
      toast.success('Mapeo creado')
    },
    onError: (error: any) => {
      toast.error('Error al crear mapeo', error.response?.data?.detail || error.message)
    },
  })

  // Mutation para eliminar mapeo
  const deleteMappingMutation = useMutation({
    mutationFn: (mappingId: number) => supplierCatalogsApi.deleteMapping(mappingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-mappings'] })
      queryClient.invalidateQueries({ queryKey: ['supplier-products-mapping'] })
      toast.success('Mapeo eliminado')
    },
    onError: (error: any) => {
      toast.error('Error al eliminar mapeo', error.response?.data?.detail || error.message)
    },
  })

  // Funciones auxiliares
  const resetSupplierForm = () => {
    setSupplierForm({
      code: '',
      name: '',
      email: '',
      phone: '',
      notes: '',
      csv_delimiter: 'auto',
      csv_encoding: 'auto',
      csv_has_header: true,
      catalog_url: '',
      import_frequency_days: 0,
    })
    resetSupplierTouched()
  }

  const openEditModal = (supplier: Supplier) => {
    setEditingSupplier(supplier)
    setEditForm({
      code: supplier.code,
      name: supplier.name,
      email: supplier.email || '',
      phone: supplier.phone || '',
      notes: supplier.notes || '',
      csv_delimiter: supplier.csv_delimiter,
      csv_encoding: supplier.csv_encoding,
      csv_has_header: supplier.csv_has_header,
      catalog_url: supplier.catalog_url || '',
      import_frequency_days: supplier.import_frequency_days,
    })
    setShowEditModal(true)
  }

  // Estados para carga de preview
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const resetImport = () => {
    setImportUrl('')
    setImportFile(null)
    setImportLocalPath('')
    setImportSource('url')
    setImportPreview(null)
    setColumnMapping({})
    setImportStep('select')
    setImportResult(null)
    setIsImporting(false)
    setIsLoadingPreview(false)
    setPreviewError(null)
  }

  // Detectar si es archivo JSON por extensión
  const isJsonFile = (path: string) => path.toLowerCase().endsWith('.json')

  const handlePreviewCsv = async () => {
    if (!selectedSupplier) return

    setIsLoadingPreview(true)
    setPreviewError(null)

    try {
      let preview: ImportPreview
      if (importFile) {
        preview = await supplierCatalogsApi.previewCsv(
          selectedSupplier.id,
          importFile,
          selectedSupplier.csv_delimiter,
          selectedSupplier.csv_encoding,
          selectedSupplier.csv_has_header
        )
      } else if (importUrl) {
        console.log('Calling previewCsvFromUrl with:', {
          supplierId: selectedSupplier.id,
          url: importUrl,
          delimiter: selectedSupplier.csv_delimiter,
          encoding: selectedSupplier.csv_encoding,
          hasHeader: selectedSupplier.csv_has_header
        })
        preview = await supplierCatalogsApi.previewCsvFromUrl(
          selectedSupplier.id,
          importUrl,
          selectedSupplier.csv_delimiter,
          selectedSupplier.csv_encoding,
          selectedSupplier.csv_has_header
        )
        console.log('Preview result:', preview)
      } else {
        setIsLoadingPreview(false)
        return
      }

      setImportPreview(preview)
      if (preview.success) {
        setImportStep('mapping')
        // Auto-detectar mapeo si el proveedor ya tiene uno guardado
        if (selectedSupplier.column_mapping) {
          setColumnMapping(selectedSupplier.column_mapping)
        }
      } else {
        setPreviewError(preview.error || 'Error al obtener vista previa del CSV')
      }
    } catch (error: any) {
      console.error('Error previewing CSV:', error)
      setPreviewError(error?.response?.data?.detail || error?.message || 'Error al obtener vista previa')
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const handleImport = async () => {
    console.log('handleImport called', { selectedSupplier, columnMapping, importFile, importUrl, importLocalPath, importSource })

    // Para archivos locales JSON no se necesita mapeo
    const isLocalJson = importSource === 'local' && isJsonFile(importLocalPath)

    if (!selectedSupplier) {
      console.log('No supplier selected')
      return
    }

    // Solo requerir mapeo si no es JSON local
    if (!isLocalJson && (columnMapping.reference === undefined || columnMapping.name === undefined)) {
      console.log('Validation failed:', {
        selectedSupplier: !!selectedSupplier,
        reference: columnMapping.reference,
        name: columnMapping.name
      })
      return
    }

    setIsImporting(true)
    try {
      let result: ImportResult

      if (importSource === 'local' && importLocalPath) {
        // Importar desde archivo local
        console.log('Importing from local file:', importLocalPath)
        result = await supplierCatalogsApi.importLocalFile(
          selectedSupplier.id,
          importLocalPath,
          isLocalJson ? undefined : columnMapping
        )
      } else if (importFile) {
        console.log('Importing from uploaded file...')
        result = await supplierCatalogsApi.importCsv(
          selectedSupplier.id,
          importFile,
          columnMapping
        )
      } else if (importUrl) {
        console.log('Importing from URL:', importUrl)
        result = await supplierCatalogsApi.importCsvFromUrl(
          selectedSupplier.id,
          importUrl,
          columnMapping
        )
      } else {
        console.log('No file, URL or local path provided')
        setIsImporting(false)
        return
      }

      console.log('Import result:', result)
      setImportResult(result)
      setImportStep('result')
      queryClient.invalidateQueries({ queryKey: ['supplier-products'] })
      queryClient.invalidateQueries({ queryKey: ['supplier-stats'] })
      queryClient.invalidateQueries({ queryKey: ['supplier-global-stats'] })
    } catch (error: any) {
      console.error('Error importing:', error)
      // Show error as result
      setImportResult({
        success: false,
        error: error?.response?.data?.detail || error?.message || 'Error desconocido al importar',
        total_rows: 0,
        products_created: 0,
        products_updated: 0,
        errors: 1
      })
      setImportStep('result')
    } finally {
      setIsImporting(false)
    }
  }

  const handleDeleteSupplier = (supplier: Supplier) => {
    if (confirm(`Eliminar proveedor "${supplier.name}" y todos sus productos?`)) {
      deleteSupplierMutation.mutate(supplier.id)
    }
  }

  const stats = globalStatsQuery.data

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Panel izquierdo - Lista de proveedores */}
      <div className="w-80 border-r border-slate-200 dark:border-slate-700 flex flex-col bg-white dark:bg-slate-800">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Proveedores
            </h2>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary btn-sm"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Stats globales */}
          {stats && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-primary-50 dark:bg-primary-900/30 rounded p-2">
                <div className="text-primary-600 dark:text-primary-400 font-semibold">
                  {stats.total_products}
                </div>
                <div className="text-slate-500 dark:text-slate-400">Productos</div>
              </div>
              <div className="bg-success-50 dark:bg-success-900/30 rounded p-2">
                <div className="text-success-600 dark:text-success-400 font-semibold">
                  {stats.total_published}
                </div>
                <div className="text-slate-500 dark:text-slate-400">Publicados</div>
              </div>
            </div>
          )}
        </div>

        {/* Lista de proveedores */}
        <div className="flex-1 overflow-y-auto">
          {suppliersQuery.isLoading ? (
            <div className="p-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : suppliersQuery.data?.length === 0 ? (
            <EmptyState
              iconType="products"
              title="No hay proveedores"
              description="Crea un proveedor para empezar a gestionar catalogos"
              action={{
                label: 'Crear proveedor',
                onClick: () => setShowCreateModal(true)
              }}
            />
          ) : (
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {suppliersQuery.data?.map((supplier) => (
                <div
                  key={supplier.id}
                  className={`p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 ${
                    selectedSupplier?.id === supplier.id
                      ? 'bg-primary-50 dark:bg-primary-900/30 border-l-4 border-primary-500'
                      : ''
                  }`}
                  onClick={() => {
                    setSelectedSupplier(supplier)
                    setSelectedProductIds(new Set()) // Limpiar selección al cambiar de proveedor
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-slate-900 dark:text-white">
                        {supplier.name}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {supplier.code}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteSupplier(supplier)
                      }}
                      className="p-1 text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/30 rounded opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Panel derecho - Contenido del proveedor */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!selectedSupplier ? (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            <div className="text-center">
              <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p>Selecciona un proveedor para ver sus productos</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header del proveedor */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                    {selectedSupplier.name}
                  </h1>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Codigo: {selectedSupplier.code}
                  </p>
                  {/* Estado de importación automática */}
                  {selectedSupplier.catalog_url && selectedSupplier.import_frequency_days > 0 && (
                    <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                      ⏰ Importación automática cada {selectedSupplier.import_frequency_days} días
                      {selectedSupplier.last_auto_import && (
                        <span className="text-slate-500 dark:text-slate-400">
                          {' '}· Última: {new Date(selectedSupplier.last_auto_import).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(selectedSupplier)}
                    className="btn btn-secondary"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Editar
                  </button>
                  <button
                    onClick={() => {
                      setMappingSearch('')
                      setMappingProductSearch('')
                      setSelectedFactusolRef(null)
                      setMappingPage(1)
                      setShowMappingModal(true)
                    }}
                    className="btn btn-secondary"
                  >
                    <Link className="w-4 h-4 mr-2" />
                    Mapear Referencias
                  </button>
                  <button
                    onClick={() => {
                      resetImport()
                      // Autocompletar URL si el proveedor tiene una configurada
                      if (selectedSupplier?.catalog_url) {
                        setImportUrl(selectedSupplier.catalog_url)
                      }
                      setShowImportModal(true)
                    }}
                    className="btn btn-primary"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Importar Catálogo
                  </button>
                </div>
              </div>

              {/* Stats del proveedor */}
              {supplierStatsQuery.data && (
                <div className="grid grid-cols-5 gap-4">
                  <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-3">
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">
                      {supplierStatsQuery.data.total_products}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      Total productos
                    </div>
                  </div>
                  <div className="bg-success-50 dark:bg-success-900/30 rounded-lg p-3">
                    <div className="text-2xl font-bold text-success-600">
                      {supplierStatsQuery.data.published_prestashop}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      En PrestaShop
                    </div>
                  </div>
                  <div className="bg-primary-50 dark:bg-primary-900/30 rounded-lg p-3">
                    <div className="text-2xl font-bold text-primary-600">
                      {supplierStatsQuery.data.published_factusol}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      En Factusol
                    </div>
                  </div>
                  <div className="bg-accent-50 dark:bg-accent-900/30 rounded-lg p-3">
                    <div className="text-2xl font-bold text-accent-600">
                      {supplierStatsQuery.data.with_stock}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      Con stock
                    </div>
                  </div>
                  <div className="bg-warning-50 dark:bg-warning-900/30 rounded-lg p-3">
                    <div className="text-2xl font-bold text-warning-600">
                      {supplierStatsQuery.data.unpublished}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      Sin publicar
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Buscador y filtros de productos */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 space-y-3">
              {/* Fila principal: búsqueda general y ordenación */}
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value)
                      setProductPage(1)
                    }}
                    placeholder="Buscar por referencia, nombre, EAN..."
                    className="input pl-10"
                  />
                </div>
                <div className="w-64 relative">
                  <XCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-danger-400" />
                  <input
                    type="text"
                    value={productExclude}
                    onChange={(e) => {
                      setProductExclude(e.target.value)
                      setProductPage(1)
                    }}
                    placeholder="No contiene..."
                    className="input pl-10 border-danger-200 dark:border-danger-800 focus:border-danger-400"
                  />
                </div>
                {/* Selector de ordenación */}
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-')
                    setSortBy(field)
                    setSortOrder(order)
                    setProductPage(1)
                  }}
                  className="input w-48"
                >
                  <option value="stock-desc">Stock (mayor a menor)</option>
                  <option value="stock-asc">Stock (menor a mayor)</option>
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="cost_price-desc">Precio (mayor a menor)</option>
                  <option value="cost_price-asc">Precio (menor a mayor)</option>
                  <option value="supplier_reference-asc">Referencia (A-Z)</option>
                </select>
                <button
                  onClick={() => productsQuery.refetch()}
                  className="btn btn-secondary"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>

              {/* Fila de filtros específicos */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                    Filtrar por referencia
                  </label>
                  <input
                    type="text"
                    value={filterReference}
                    onChange={(e) => {
                      setFilterReference(e.target.value)
                      setProductPage(1)
                    }}
                    placeholder="Contiene texto en referencia..."
                    className="input text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
                    Filtrar por categoría
                  </label>
                  <input
                    type="text"
                    value={filterCategory}
                    onChange={(e) => {
                      setFilterCategory(e.target.value)
                      setProductPage(1)
                    }}
                    placeholder="Contiene texto en categoría..."
                    className="input text-sm"
                  />
                </div>
                {(filterReference || filterCategory || hideMapped) && (
                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setFilterReference('')
                        setFilterCategory('')
                        setHideMapped(false)
                        setProductPage(1)
                      }}
                      className="btn btn-secondary text-sm"
                      title="Limpiar filtros"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Checkbox para ocultar productos vinculados */}
              <div className="flex items-center gap-2 mt-3">
                <input
                  type="checkbox"
                  id="hideMapped"
                  checked={hideMapped}
                  onChange={(e) => {
                    setHideMapped(e.target.checked)
                    setProductPage(1)
                  }}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="hideMapped" className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
                  Ocultar productos ya vinculados
                </label>
              </div>
            </div>

            {/* Lista de productos */}
            <div className="flex-1 overflow-y-auto">
              {productsQuery.isLoading ? (
                <div className="p-8 text-center text-slate-500">Cargando productos...</div>
              ) : productsQuery.data?.products.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No se encontraron productos
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700 sticky top-0">
                    <tr>
                      <th className="px-2 py-3 w-10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            const eligibleProducts = productsQuery.data?.products.filter(
                              (p) => !p.published_prestashop && !p.published_factusol && !p.mapped_factusol_reference
                            ) || []
                            const allSelected = eligibleProducts.length > 0 &&
                              eligibleProducts.every((p) => selectedProductIds.has(p.id))
                            if (allSelected) {
                              setSelectedProductIds(new Set())
                            } else {
                              setSelectedProductIds(new Set(eligibleProducts.map((p) => p.id)))
                            }
                          }}
                          className="p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                          title={selectedProductIds.size > 0 ? "Deseleccionar todos" : "Seleccionar todos elegibles"}
                        >
                          {(() => {
                            const eligibleProducts = productsQuery.data?.products.filter(
                              (p) => !p.published_prestashop && !p.published_factusol && !p.mapped_factusol_reference
                            ) || []
                            const allSelected = eligibleProducts.length > 0 &&
                              eligibleProducts.every((p) => selectedProductIds.has(p.id))
                            return allSelected ? (
                              <CheckSquare className="w-4 h-4 text-warning-600" />
                            ) : (
                              <Square className="w-4 h-4 text-slate-400" />
                            )
                          })()}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300">
                        Referencia
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300">
                        Nombre
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 max-w-[200px]">
                        Categoría
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300">
                        Coste
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-300">
                        PVP
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300">
                        Stock
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300">
                        Estado
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {productsQuery.data?.products.map((product) => (
                      <Fragment key={product.id}>
                        <tr
                          className={`hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer ${
                            selectedProductIds.has(product.id) ? 'bg-warning-50 dark:bg-warning-900/20' : ''
                          }`}
                          onClick={() =>
                            setExpandedProduct(
                              expandedProduct === product.id ? null : product.id
                            )
                          }
                        >
                          <td className="px-2 py-3 w-10">
                            {/* Solo mostrar checkbox si el producto es elegible para crear */}
                            {!product.published_prestashop && !product.published_factusol && !product.mapped_factusol_reference ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  const newSet = new Set(selectedProductIds)
                                  if (newSet.has(product.id)) {
                                    newSet.delete(product.id)
                                  } else {
                                    newSet.add(product.id)
                                  }
                                  setSelectedProductIds(newSet)
                                }}
                                className="p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                              >
                                {selectedProductIds.has(product.id) ? (
                                  <CheckSquare className="w-4 h-4 text-warning-600" />
                                ) : (
                                  <Square className="w-4 h-4 text-slate-400" />
                                )}
                              </button>
                            ) : (
                              <div className="w-6" />
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {expandedProduct === product.id ? (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                              <div>
                                <span className="font-mono text-sm text-slate-900 dark:text-white">
                                  {product.supplier_reference}
                                </span>
                                {product.mapped_factusol_reference && (
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <Link className="w-3 h-3 text-accent-500" />
                                    <span className="font-mono text-xs text-accent-600 dark:text-purple-400">
                                      {product.mapped_factusol_reference}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-slate-900 dark:text-white line-clamp-1">
                              {product.name}
                            </div>
                            {product.brand && (
                              <div className="text-xs text-slate-500">{product.brand}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 max-w-[200px]">
                            {product.category ? (
                              <div
                                className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2"
                                title={product.category}
                              >
                                {product.category}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">
                            {formatPrice(product.cost_price)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-success-600">
                            {product.rrp_price ? formatPrice(product.rrp_price) : '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                product.stock > 0
                                  ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                  : 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400'
                              }`}
                            >
                              {product.stock}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              {/* Indicador de mapeo/vinculación */}
                              {product.mapped_factusol_reference ? (
                                <span
                                  className="p-1 bg-accent-100 dark:bg-purple-900/30 rounded"
                                  title={`Vinculado a ${product.mapped_factusol_reference}`}
                                >
                                  <Link className="w-4 h-4 text-accent-600" />
                                </span>
                              ) : (
                                <span
                                  className="p-1 bg-slate-100 dark:bg-slate-700 rounded"
                                  title="Sin vincular"
                                >
                                  <Unlink className="w-4 h-4 text-slate-400" />
                                </span>
                              )}
                              {/* Indicador PrestaShop - clickeable si está publicado */}
                              {product.published_prestashop ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setUnpublishConfirm({
                                      show: true,
                                      productId: product.id,
                                      productName: product.name,
                                      target: 'prestashop'
                                    })
                                  }}
                                  className="p-1 rounded bg-success-100 dark:bg-green-900/30 hover:bg-success-200 dark:hover:bg-success-800/50 cursor-pointer transition-colors"
                                  title="Publicado en PrestaShop - Click para desmarcar"
                                >
                                  <Store className="w-4 h-4 text-success-600" />
                                </button>
                              ) : (
                                <span
                                  className="p-1 bg-slate-100 dark:bg-slate-700 rounded"
                                  title="No publicado en PrestaShop"
                                >
                                  <Store className="w-4 h-4 text-slate-400" />
                                </span>
                              )}
                              {/* Indicador Factusol - clickeable si está publicado */}
                              {product.published_factusol ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setUnpublishConfirm({
                                      show: true,
                                      productId: product.id,
                                      productName: product.name,
                                      target: 'factusol'
                                    })
                                  }}
                                  className="p-1 rounded bg-primary-100 dark:bg-blue-900/30 hover:bg-primary-200 dark:hover:bg-primary-800/50 cursor-pointer transition-colors"
                                  title="Publicado en Factusol - Click para desmarcar"
                                >
                                  <Package className="w-4 h-4 text-primary-600" />
                                </button>
                              ) : (
                                <span
                                  className="p-1 bg-slate-100 dark:bg-slate-700 rounded"
                                  title="No publicado en Factusol"
                                >
                                  <Package className="w-4 h-4 text-slate-400" />
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              {/* Botón Crear con IA - solo si no está publicado en ningún sitio ni mapeado */}
                              {!product.published_prestashop && !product.published_factusol && !product.mapped_factusol_reference && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    // Navegar a ProductCreatorPage con datos pre-rellenados
                                    // Usamos el code del proveedor (CODPRO de Factusol) para pre-seleccionar
                                    // Incluimos supplier_id y product_id para cargar imágenes locales
                                    const params = new URLSearchParams({
                                      name: product.name || '',
                                      reference: product.supplier_reference || '',
                                      cost_price: product.cost_price?.toString() || '',
                                      ean: product.ean || '',
                                      supplier_ref: product.supplier_reference || '',
                                      supplier_code: selectedSupplier?.code || '',
                                      supplier_name: selectedSupplier?.name || '',
                                      supplier_id: selectedSupplier?.id?.toString() || '',
                                      product_id: product.id?.toString() || '',
                                    })
                                    navigate(`/products/create?${params.toString()}`)
                                  }}
                                  className="p-1 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/30 rounded"
                                  title="Crear producto con IA"
                                >
                                  <Sparkles className="w-4 h-4" />
                                </button>
                              )}
                              {!product.published_prestashop && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    publishMutation.mutate({
                                      supplierId: selectedSupplier.id,
                                      productId: product.id,
                                      target: 'prestashop',
                                    })
                                  }}
                                  className="p-1 text-success-600 hover:bg-success-50 dark:hover:bg-success-900/30 rounded"
                                  title="Publicar en PrestaShop"
                                >
                                  <Store className="w-4 h-4" />
                                </button>
                              )}
                              {!product.published_factusol && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    publishMutation.mutate({
                                      supplierId: selectedSupplier.id,
                                      productId: product.id,
                                      target: 'factusol',
                                    })
                                  }}
                                  className="p-1 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded"
                                  title="Publicar en Factusol"
                                >
                                  <Package className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* Fila expandida con historico de precios */}
                        {expandedProduct === product.id && (
                          <tr>
                            <td colSpan={7} className="bg-slate-50 dark:bg-slate-900 p-4">
                              <div className="grid grid-cols-2 gap-4">
                                {/* Detalles del producto */}
                                <div>
                                  <h4 className="font-semibold mb-2 text-slate-900 dark:text-white">
                                    Detalles
                                  </h4>
                                  <div className="space-y-1 text-sm">
                                    {product.ean && (
                                      <div>
                                        <span className="text-slate-500">EAN:</span>{' '}
                                        <span className="font-mono">{product.ean}</span>
                                      </div>
                                    )}
                                    {product.category && (
                                      <div>
                                        <span className="text-slate-500">Categoria:</span>{' '}
                                        {product.category}
                                      </div>
                                    )}
                                    {product.description && (
                                      <div>
                                        <span className="text-slate-500">Descripcion:</span>
                                        <p className="text-slate-700 dark:text-slate-300 mt-1 line-clamp-3">
                                          {product.description}
                                        </p>
                                      </div>
                                    )}
                                    {product.last_seen && (
                                      <div>
                                        <span className="text-slate-500">Ultima actualizacion:</span>{' '}
                                        {new Date(product.last_seen).toLocaleDateString()}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Historico de precios */}
                                <div>
                                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-slate-900 dark:text-white">
                                    <TrendingUp className="w-4 h-4" />
                                    Historico de Precios
                                  </h4>
                                  {priceHistoryQuery.isLoading ? (
                                    <div className="text-slate-500">Cargando...</div>
                                  ) : priceHistoryQuery.data?.length === 0 ? (
                                    <div className="text-slate-500">Sin historico</div>
                                  ) : (
                                    <div className="max-h-40 overflow-y-auto">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="text-xs text-slate-600 dark:text-slate-300">
                                            <th className="text-left pb-1">Fecha</th>
                                            <th className="text-right pb-1">Coste</th>
                                            <th className="text-right pb-1">PVP</th>
                                            <th className="text-right pb-1">Stock</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {priceHistoryQuery.data?.map((entry, idx) => (
                                            <tr key={idx} className="border-t border-slate-200 dark:border-slate-700">
                                              <td className="py-1 text-gray-900 dark:text-gray-100">{entry.date}</td>
                                              <td className="text-right font-mono text-gray-900 dark:text-gray-100">
                                                {formatPrice(entry.cost_price)}
                                              </td>
                                              <td className="text-right font-mono text-success-600 dark:text-success-400">
                                                {entry.rrp_price
                                                  ? formatPrice(entry.rrp_price)
                                                  : '-'}
                                              </td>
                                              <td className="text-right text-gray-900 dark:text-gray-100">{entry.stock}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Paginacion e información de resultados */}
            {productsQuery.data && (
              <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between">
                <div className="text-sm text-slate-500">
                  {productsQuery.data.total === 0 ? (
                    <span>No se encontraron productos</span>
                  ) : productsQuery.data.total <= PRODUCTS_PAGE_SIZE ? (
                    <span>
                      Mostrando <span className="font-medium text-slate-700 dark:text-slate-300">{productsQuery.data.total}</span> producto{productsQuery.data.total !== 1 ? 's' : ''}
                      {(productSearch || filterReference || filterCategory || hideMapped) && (
                        <span className="text-blue-600 dark:text-blue-400"> (filtrado)</span>
                      )}
                    </span>
                  ) : (
                    <span>
                      Mostrando <span className="font-medium text-slate-700 dark:text-slate-300">{(productPage - 1) * PRODUCTS_PAGE_SIZE + 1}-{Math.min(productPage * PRODUCTS_PAGE_SIZE, productsQuery.data.total)}</span> de{' '}
                      <span className="font-medium text-slate-700 dark:text-slate-300">{productsQuery.data.total}</span> productos
                      {(productSearch || filterReference || filterCategory || hideMapped) && (
                        <span className="text-blue-600 dark:text-blue-400"> (filtrado)</span>
                      )}
                    </span>
                  )}
                </div>
                {productsQuery.data.total > PRODUCTS_PAGE_SIZE && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setProductPage(productPage - 1)}
                      disabled={productPage === 1}
                      className="btn btn-secondary btn-sm"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={() => setProductPage(productPage + 1)}
                      disabled={productPage * PRODUCTS_PAGE_SIZE >= productsQuery.data.total}
                      className="btn btn-secondary btn-sm"
                    >
                      Siguiente
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal crear proveedor */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Nuevo Proveedor
            </h3>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${supplierErrors.code?.hasError ? 'text-danger-600 dark:text-danger-400' : 'text-slate-700 dark:text-slate-300'}`}>
                  Codigo <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  value={supplierForm.code}
                  onChange={(e) =>
                    setSupplierForm({ ...supplierForm, code: e.target.value.toUpperCase() })
                  }
                  onBlur={() => getSupplierFieldProps('code').onBlur()}
                  className={`input ${supplierErrors.code?.hasError ? 'border-red-500 dark:border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                  placeholder="GC"
                  maxLength={10}
                  aria-invalid={supplierErrors.code?.hasError}
                />
                {supplierErrors.code?.hasError && (
                  <p className="text-sm text-danger-600 dark:text-danger-400 mt-1" role="alert">
                    {supplierErrors.code.message}
                  </p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${supplierErrors.name?.hasError ? 'text-danger-600 dark:text-danger-400' : 'text-slate-700 dark:text-slate-300'}`}>
                  Nombre <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  value={supplierForm.name}
                  onChange={(e) =>
                    setSupplierForm({ ...supplierForm, name: e.target.value })
                  }
                  onBlur={() => getSupplierFieldProps('name').onBlur()}
                  className={`input ${supplierErrors.name?.hasError ? 'border-red-500 dark:border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                  placeholder="Green Cell"
                  maxLength={100}
                  aria-invalid={supplierErrors.name?.hasError}
                />
                {supplierErrors.name?.hasError && (
                  <p className="text-sm text-danger-600 dark:text-danger-400 mt-1" role="alert">
                    {supplierErrors.name.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Delimitador CSV
                  </label>
                  <select
                    value={supplierForm.csv_delimiter}
                    onChange={(e) =>
                      setSupplierForm({ ...supplierForm, csv_delimiter: e.target.value })
                    }
                    className="input"
                  >
                    <option value="auto">Automatico</option>
                    <option value=";">Punto y coma (;)</option>
                    <option value=",">Coma (,)</option>
                    <option value="\t">Tabulador</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Encoding
                  </label>
                  <select
                    value={supplierForm.csv_encoding}
                    onChange={(e) =>
                      setSupplierForm({ ...supplierForm, csv_encoding: e.target.value })
                    }
                    className="input"
                  >
                    <option value="auto">Automatico</option>
                    <option value="utf-8">UTF-8</option>
                    <option value="latin-1">Latin-1</option>
                    <option value="cp1252">Windows-1252</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="has_header"
                  checked={supplierForm.csv_has_header}
                  onChange={(e) =>
                    setSupplierForm({ ...supplierForm, csv_has_header: e.target.checked })
                  }
                  className="rounded"
                />
                <label
                  htmlFor="has_header"
                  className="text-sm text-slate-700 dark:text-slate-300"
                >
                  El CSV tiene fila de cabecera
                </label>
              </div>

              {/* Importacion automatica */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  Importacion Automatica
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className={`block text-sm mb-1 ${supplierErrors.catalog_url?.hasError ? 'text-danger-600 dark:text-danger-400' : 'text-slate-600 dark:text-slate-400'}`}>
                      URL o Ruta del Catálogo
                    </label>
                    <input
                      type="text"
                      value={supplierForm.catalog_url}
                      onChange={(e) =>
                        setSupplierForm({ ...supplierForm, catalog_url: e.target.value })
                      }
                      onBlur={() => getSupplierFieldProps('catalog_url').onBlur()}
                      placeholder="https://ejemplo.com/catalogo.csv o /ruta/local/catalogo.json"
                      className={`input w-full font-mono text-sm ${supplierErrors.catalog_url?.hasError ? 'border-red-500 dark:border-red-400 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                      aria-invalid={supplierErrors.catalog_url?.hasError}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      URL remota (http://...) o ruta local del servidor (/ruta/archivo.json)
                    </p>
                    {supplierErrors.catalog_url?.hasError && (
                      <p className="text-sm text-danger-600 dark:text-danger-400 mt-1" role="alert">
                        {supplierErrors.catalog_url.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">
                      Frecuencia de importacion (dias)
                    </label>
                    <select
                      value={supplierForm.import_frequency_days}
                      onChange={(e) =>
                        setSupplierForm({ ...supplierForm, import_frequency_days: parseInt(e.target.value) })
                      }
                      className="input"
                    >
                      <option value={0}>Desactivado</option>
                      <option value={1}>Cada dia</option>
                      <option value={2}>Cada 2 dias</option>
                      <option value={3}>Cada 3 dias</option>
                      <option value={7}>Cada semana</option>
                      <option value={14}>Cada 2 semanas</option>
                      <option value={30}>Cada mes</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Requiere URL y mapeo de columnas configurado
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  resetSupplierForm()
                }}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  touchAllSupplierFields()
                  if (isSupplierFormValid) {
                    createSupplierMutation.mutate(supplierForm)
                  } else {
                    toast.warning('Por favor, corrige los errores del formulario')
                  }
                }}
                disabled={!supplierForm.code || !supplierForm.name || createSupplierMutation.isPending}
                className="btn btn-primary"
              >
                {createSupplierMutation.isPending ? 'Creando...' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal editar proveedor */}
      {showEditModal && editingSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Editar Proveedor: {editingSupplier.name}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Codigo *
                </label>
                <input
                  type="text"
                  value={editForm.code}
                  onChange={(e) =>
                    setEditForm({ ...editForm, code: e.target.value })
                  }
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                  className="input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Delimitador CSV
                  </label>
                  <select
                    value={editForm.csv_delimiter}
                    onChange={(e) =>
                      setEditForm({ ...editForm, csv_delimiter: e.target.value })
                    }
                    className="input"
                  >
                    <option value="auto">Automatico</option>
                    <option value=";">Punto y coma (;)</option>
                    <option value=",">Coma (,)</option>
                    <option value="\t">Tabulador</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Encoding
                  </label>
                  <select
                    value={editForm.csv_encoding}
                    onChange={(e) =>
                      setEditForm({ ...editForm, csv_encoding: e.target.value })
                    }
                    className="input"
                  >
                    <option value="auto">Automatico</option>
                    <option value="utf-8">UTF-8</option>
                    <option value="latin-1">Latin-1</option>
                    <option value="cp1252">Windows-1252</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit_has_header"
                  checked={editForm.csv_has_header}
                  onChange={(e) =>
                    setEditForm({ ...editForm, csv_has_header: e.target.checked })
                  }
                  className="rounded"
                />
                <label
                  htmlFor="edit_has_header"
                  className="text-sm text-slate-700 dark:text-slate-300"
                >
                  El CSV tiene fila de cabecera
                </label>
              </div>

              {/* Importacion automatica */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  Importacion Automatica
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">
                      URL o Ruta del Catálogo
                    </label>
                    <input
                      type="text"
                      value={editForm.catalog_url}
                      onChange={(e) =>
                        setEditForm({ ...editForm, catalog_url: e.target.value })
                      }
                      placeholder="https://ejemplo.com/catalogo.csv o /ruta/local/catalogo.json"
                      className="input w-full font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      URL remota (http://...) o ruta local del servidor (/ruta/archivo.json)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">
                      Frecuencia de importacion (dias)
                    </label>
                    <select
                      value={editForm.import_frequency_days}
                      onChange={(e) =>
                        setEditForm({ ...editForm, import_frequency_days: parseInt(e.target.value) })
                      }
                      className="input"
                    >
                      <option value={0}>Desactivado</option>
                      <option value={1}>Cada dia</option>
                      <option value={2}>Cada 2 dias</option>
                      <option value={3}>Cada 3 dias</option>
                      <option value={7}>Cada semana</option>
                      <option value={14}>Cada 2 semanas</option>
                      <option value={30}>Cada mes</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Requiere URL y mapeo de columnas configurado
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingSupplier(null)
                }}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (editingSupplier) {
                    updateSupplierMutation.mutate({
                      id: editingSupplier.id,
                      data: {
                        code: editForm.code,
                        name: editForm.name,
                        email: editForm.email || null,
                        phone: editForm.phone || null,
                        notes: editForm.notes || null,
                        csv_delimiter: editForm.csv_delimiter,
                        csv_encoding: editForm.csv_encoding,
                        csv_has_header: editForm.csv_has_header,
                        catalog_url: editForm.catalog_url || null,
                        import_frequency_days: editForm.import_frequency_days,
                      },
                    })
                  }
                }}
                disabled={!editForm.code || !editForm.name || updateSupplierMutation.isPending}
                className="btn btn-primary"
              >
                {updateSupplierMutation.isPending ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal importar CSV */}
      {showImportModal && selectedSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="import-modal-title">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 id="import-modal-title" className="text-lg font-semibold text-slate-900 dark:text-white">
                  Importar Catalogo - {selectedSupplier.name}
                </h3>
                <button
                  onClick={() => {
                    setShowImportModal(false)
                    resetImport()
                  }}
                  className="p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                  aria-label="Cerrar modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Indicador de pasos del wizard */}
              <div className="flex items-center justify-center gap-2">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    importStep === 'select'
                      ? 'bg-primary-600 text-white'
                      : 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                  }`}>
                    1
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    importStep === 'select' ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'
                  }`}>
                    Seleccionar
                  </span>
                </div>
                <div className={`w-12 h-0.5 ${
                  importStep !== 'select' ? 'bg-primary-600' : 'bg-slate-300 dark:bg-slate-600'
                }`} />
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    importStep === 'mapping'
                      ? 'bg-primary-600 text-white'
                      : importStep === 'result'
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                  }`}>
                    2
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    importStep === 'mapping' ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'
                  }`}>
                    Mapear
                  </span>
                </div>
                <div className={`w-12 h-0.5 ${
                  importStep === 'result' ? 'bg-primary-600' : 'bg-slate-300 dark:bg-slate-600'
                }`} />
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    importStep === 'result'
                      ? 'bg-primary-600 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                  }`}>
                    3
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    importStep === 'result' ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'
                  }`}>
                    Resultado
                  </span>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Paso 1: Seleccionar archivo */}
              {importStep === 'select' && (
                <div className="space-y-6">
                  {/* Selector de tipo de fuente */}
                  <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-700 rounded-lg">
                    <button
                      onClick={() => {
                        setImportSource('file')
                        setImportUrl('')
                        setImportLocalPath('')
                      }}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        importSource === 'file'
                          ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow'
                          : 'text-slate-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      Subir archivo
                    </button>
                    <button
                      onClick={() => {
                        setImportSource('url')
                        setImportFile(null)
                        setImportLocalPath('')
                      }}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        importSource === 'url'
                          ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow'
                          : 'text-slate-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      URL remota
                    </button>
                    <button
                      onClick={() => {
                        setImportSource('local')
                        setImportFile(null)
                        setImportUrl('')
                      }}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                        importSource === 'local'
                          ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow'
                          : 'text-slate-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      Archivo local
                    </button>
                  </div>

                  {/* Opción: Subir archivo */}
                  {importSource === 'file' && (
                    <div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept=".csv,.txt,.xlsx,.xls,.json"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            setImportFile(e.target.files[0])
                          }
                        }}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className={`w-full p-8 border-2 border-dashed rounded-lg text-center transition-colors ${
                          importFile
                            ? 'border-green-500 bg-success-50 dark:bg-success-900/20'
                            : 'border-slate-300 dark:border-slate-600 hover:border-primary-500'
                        }`}
                      >
                        {importFile ? (
                          <div className="text-success-600">
                            <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                            <div className="font-medium">{importFile.name}</div>
                            <div className="text-sm text-slate-500">
                              {(importFile.size / 1024).toFixed(1)} KB
                            </div>
                          </div>
                        ) : (
                          <div className="text-slate-500">
                            <Upload className="w-8 h-8 mx-auto mb-2" />
                            <div>Haz clic para seleccionar un archivo CSV, Excel o JSON</div>
                          </div>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Opción: URL remota */}
                  {importSource === 'url' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        URL del catálogo
                      </label>
                      <div className="relative">
                        <Link2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="url"
                          value={importUrl}
                          onChange={(e) => setImportUrl(e.target.value)}
                          placeholder="https://example.com/catalog.csv"
                          className="input pl-10"
                        />
                      </div>
                    </div>
                  )}

                  {/* Opción: Archivo local del servidor */}
                  {importSource === 'local' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Ruta del archivo en el servidor
                      </label>
                      <input
                        type="text"
                        value={importLocalPath}
                        onChange={(e) => setImportLocalPath(e.target.value)}
                        placeholder="/ruta/al/archivo/catalogo.json"
                        className="input font-mono text-sm"
                      />
                      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                        Ruta absoluta al archivo en el servidor. Soporta JSON, CSV y Excel.
                      </p>

                      {/* Mostrar info si es JSON */}
                      {importLocalPath && isJsonFile(importLocalPath) && (
                        <div className="mt-3 p-3 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                          <div className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                            Archivo JSON detectado
                          </div>
                          <p className="text-xs text-primary-600 dark:text-primary-400">
                            Los archivos JSON se importan automáticamente sin necesidad de mapear columnas.
                            Se detectan campos como: model, sku, name, price, availability, category, etc.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Paso 2: Mapeo de columnas */}
              {importStep === 'mapping' && importPreview && (
                <div className="space-y-4">
                  <div className="bg-primary-50 dark:bg-primary-900/30 text-blue-700 dark:text-blue-300 p-3 rounded-lg text-sm">
                    Selecciona que columna del CSV corresponde a cada campo. Los campos
                    marcados con * son obligatorios.
                  </div>

                  {/* Mapeo de campos */}
                  <div className="grid grid-cols-3 gap-4">
                    {FIELD_MAPPINGS.map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          {field.label} {field.required && '*'}
                        </label>
                        <select
                          value={columnMapping[field.key] ?? ''}
                          onChange={(e) => {
                            const val = e.target.value
                            if (val === '') {
                              const newMapping = { ...columnMapping }
                              delete newMapping[field.key]
                              setColumnMapping(newMapping)
                            } else {
                              setColumnMapping({
                                ...columnMapping,
                                [field.key]: parseInt(val),
                              })
                            }
                          }}
                          className="input"
                        >
                          <option value="">-- No mapear --</option>
                          {importPreview.headers?.map((header, idx) => (
                            <option key={idx} value={idx}>
                              {idx}: {header}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  {/* Preview de datos */}
                  <div>
                    <h4 className="font-medium mb-2 text-slate-900 dark:text-white">
                      Vista previa de datos
                    </h4>
                    <div className="overflow-x-auto border border-gray-200 dark:border-slate-600 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 dark:bg-slate-700">
                          <tr>
                            <th className="px-2 py-1 text-left text-slate-500 dark:text-slate-400">#</th>
                            {importPreview.headers?.map((header, idx) => (
                              <th
                                key={idx}
                                className={`px-2 py-1 text-left ${
                                  Object.values(columnMapping).includes(idx)
                                    ? 'bg-primary-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                                    : 'text-slate-500 dark:text-slate-400'
                                }`}
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800">
                          {importPreview.rows?.slice(0, 5).map((row, rowIdx) => (
                            <tr key={rowIdx} className="border-t border-gray-200 dark:border-slate-600">
                              <td className="px-2 py-1 text-slate-400 dark:text-slate-500">{rowIdx + 1}</td>
                              {row.map((cell, cellIdx) => (
                                <td
                                  key={cellIdx}
                                  className={`px-2 py-1 max-w-[150px] truncate text-gray-900 dark:text-gray-100 ${
                                    Object.values(columnMapping).includes(cellIdx)
                                      ? 'bg-primary-50 dark:bg-primary-900/30 text-blue-900 dark:text-blue-100'
                                      : ''
                                  }`}
                                  title={cell}
                                >
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Paso 3: Resultado */}
              {importStep === 'result' && importResult && (
                <div className="text-center py-8">
                  {importResult.success ? (
                    <>
                      <CheckCircle className="w-16 h-16 mx-auto text-success-500 mb-4" />
                      <h4 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
                        Importacion completada
                      </h4>
                      <div className="grid grid-cols-4 gap-4 max-w-lg mx-auto">
                        <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-3">
                          <div className="text-2xl font-bold text-slate-900 dark:text-white">
                            {importResult.total_rows}
                          </div>
                          <div className="text-xs text-slate-500">Filas procesadas</div>
                        </div>
                        <div className="bg-success-50 dark:bg-success-900/30 rounded-lg p-3">
                          <div className="text-2xl font-bold text-success-600">
                            {importResult.products_created}
                          </div>
                          <div className="text-xs text-slate-500">Nuevos</div>
                        </div>
                        <div className="bg-primary-50 dark:bg-primary-900/30 rounded-lg p-3">
                          <div className="text-2xl font-bold text-primary-600">
                            {importResult.products_updated}
                          </div>
                          <div className="text-xs text-slate-500">Actualizados</div>
                        </div>
                        <div className="bg-danger-50 dark:bg-danger-900/30 rounded-lg p-3">
                          <div className="text-2xl font-bold text-danger-600">
                            {importResult.errors}
                          </div>
                          <div className="text-xs text-slate-500">Errores</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-16 h-16 mx-auto text-danger-500 mb-4" />
                      <h4 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                        Error en la importacion
                      </h4>
                      <p className="text-danger-500">{importResult.error}</p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Footer del modal */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700">
              {/* Mensaje de error de preview */}
              {importStep === 'select' && previewError && (
                <div className="mb-3 p-3 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg">
                  <p className="text-sm text-danger-600 dark:text-danger-400">{previewError}</p>
                </div>
              )}
              <div className="flex justify-between">
              {importStep === 'select' && (
                <>
                  <button
                    onClick={() => {
                      setShowImportModal(false)
                      resetImport()
                    }}
                    className="btn btn-secondary"
                    disabled={isLoadingPreview || isImporting}
                  >
                    Cancelar
                  </button>
                  {/* Si es archivo local JSON, importar directamente */}
                  {importSource === 'local' && importLocalPath && isJsonFile(importLocalPath) ? (
                    <button
                      onClick={handleImport}
                      disabled={!importLocalPath.trim() || isImporting}
                      className="btn btn-primary flex items-center gap-2"
                    >
                      {isImporting ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Importando JSON...
                        </>
                      ) : (
                        'Importar JSON'
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={handlePreviewCsv}
                      disabled={
                        (importSource === 'file' && !importFile) ||
                        (importSource === 'url' && !importUrl) ||
                        (importSource === 'local' && !importLocalPath) ||
                        isLoadingPreview
                      }
                      className="btn btn-primary flex items-center gap-2"
                    >
                      {isLoadingPreview ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Cargando...
                        </>
                      ) : (
                        'Continuar'
                      )}
                    </button>
                  )}
                </>
              )}

              {importStep === 'mapping' && (
                <>
                  <button
                    onClick={() => setImportStep('select')}
                    className="btn btn-secondary"
                    disabled={isImporting}
                  >
                    Atras
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={
                      columnMapping.reference === undefined ||
                      columnMapping.name === undefined ||
                      isImporting
                    }
                    className="btn btn-primary"
                  >
                    {isImporting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      'Importar'
                    )}
                  </button>
                </>
              )}

              {importStep === 'result' && (
                <button
                  onClick={() => {
                    setShowImportModal(false)
                    resetImport()
                  }}
                  className="btn btn-primary ml-auto"
                >
                  Cerrar
                </button>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de mapeo de referencias */}
      {showMappingModal && selectedSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Mapeo de Referencias - {selectedSupplier.name}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Vincula las referencias de Factusol con los productos del proveedor
                </p>
              </div>
              <button
                onClick={() => setShowMappingModal(false)}
                className="p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex">
              {/* Panel izquierdo: Productos Factusol */}
              <div className="w-1/2 border-r border-slate-200 dark:border-slate-700 flex flex-col">
                <div className="p-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                  <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                    Productos Factusol
                  </h4>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={mappingSearch}
                      onChange={(e) => {
                        setMappingSearch(e.target.value)
                        setMappingPage(1)
                      }}
                      placeholder="Buscar por referencia o nombre..."
                      className="input pl-9 text-sm"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {factusolProductsQuery.isLoading ? (
                    <div className="p-4 text-center text-slate-500">Cargando...</div>
                  ) : factusolProductsQuery.data?.products?.length === 0 ? (
                    <div className="p-4 text-center text-slate-500">
                      No se encontraron productos
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-200 dark:divide-slate-700">
                      {factusolProductsQuery.data?.products?.map((product: any) => {
                        // Check if this product has a mapping with current supplier
                        const hasMapping = supplierMappingsQuery.data?.mappings?.some(
                          (m: ReferenceMapping) => m.factusol_reference === product.reference
                        )
                        const isSelected = selectedFactusolRef === product.reference

                        return (
                          <div
                            key={product.reference}
                            className={`p-3 cursor-pointer transition-colors ${
                              isSelected
                                ? 'bg-primary-50 dark:bg-primary-900/30 border-l-4 border-primary-500'
                                : hasMapping
                                ? 'bg-success-50 dark:bg-success-900/20 hover:bg-success-100 dark:hover:bg-success-900/30'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                            }`}
                            onClick={() => {
                              setSelectedFactusolRef(product.reference)
                              setMappingProductSearch('')
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-sm font-medium text-slate-900 dark:text-white">
                                    {product.reference}
                                  </span>
                                  {hasMapping && (
                                    <span className="px-1.5 py-0.5 bg-success-100 dark:bg-green-900/50 text-success-700 dark:text-green-300 text-xs rounded">
                                      Mapeado
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-slate-600 dark:text-slate-400 truncate">
                                  {product.name}
                                </div>
                              </div>
                              {isSelected && (
                                <ChevronRight className="w-5 h-5 text-primary-500" />
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Paginacion Factusol */}
                {factusolProductsQuery.data?.total > 20 && (
                  <div className="p-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-sm">
                    <span className="text-slate-500">
                      {factusolProductsQuery.data?.total} productos
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setMappingPage(Math.max(1, mappingPage - 1))}
                        disabled={mappingPage === 1}
                        className="btn btn-secondary btn-sm"
                      >
                        Ant
                      </button>
                      <button
                        onClick={() => setMappingPage(mappingPage + 1)}
                        disabled={mappingPage * 20 >= factusolProductsQuery.data?.total}
                        className="btn btn-secondary btn-sm"
                      >
                        Sig
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Panel derecho: Productos del proveedor para mapear */}
              <div className="w-1/2 flex flex-col">
                {!selectedFactusolRef ? (
                  <div className="flex-1 flex items-center justify-center text-slate-500">
                    <div className="text-center">
                      <Link className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Selecciona un producto de Factusol</p>
                      <p className="text-sm">para ver o crear mapeos</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="p-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-slate-900 dark:text-white">
                          Mapeos para: <span className="font-mono">{selectedFactusolRef}</span>
                        </h4>
                        <button
                          onClick={() => setSelectedFactusolRef(null)}
                          className="text-sm text-slate-500 hover:text-gray-700"
                        >
                          Limpiar
                        </button>
                      </div>

                      {/* Mapeos existentes */}
                      {(supplierMappingsQuery.data?.mappings?.filter(
                        (m: ReferenceMapping) => m.factusol_reference === selectedFactusolRef
                      )?.length ?? 0) > 0 && (
                        <div className="mb-3">
                          <div className="text-xs font-medium text-slate-500 mb-1">Mapeos actuales:</div>
                          <div className="space-y-1">
                            {supplierMappingsQuery.data?.mappings
                              ?.filter((m: ReferenceMapping) => m.factusol_reference === selectedFactusolRef)
                              .map((mapping: ReferenceMapping) => (
                                <div
                                  key={mapping.id}
                                  className="flex items-center justify-between p-2 bg-success-100 dark:bg-green-900/30 rounded text-sm"
                                >
                                  <div>
                                    <span className="font-mono font-medium">{mapping.supplier_reference}</span>
                                    <span className="text-slate-500 ml-2">{mapping.product_name}</span>
                                  </div>
                                  <button
                                    onClick={() => deleteMappingMutation.mutate(mapping.id)}
                                    className="p-1 text-danger-500 hover:bg-danger-100 dark:hover:bg-danger-900/30 rounded"
                                    title="Eliminar mapeo"
                                  >
                                    <Unlink className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          value={mappingProductSearch}
                          onChange={(e) => setMappingProductSearch(e.target.value)}
                          placeholder="Buscar producto del proveedor..."
                          className="input pl-9 text-sm"
                        />
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                      {supplierProductsForMappingQuery.isLoading ? (
                        <div className="p-4 text-center text-slate-500">Cargando productos...</div>
                      ) : supplierProductsForMappingQuery.isError ? (
                        <div className="p-4 text-center text-danger-500">
                          Error al cargar productos: {supplierProductsForMappingQuery.error?.message}
                        </div>
                      ) : !supplierProductsForMappingQuery.data ? (
                        <div className="p-4 text-center text-slate-500">
                          Cargando lista de productos...
                        </div>
                      ) : supplierProductsForMappingQuery.data.products?.length === 0 ? (
                        <div className="p-4 text-center text-slate-500">
                          {mappingProductSearch
                            ? `No se encontraron productos con "${mappingProductSearch}"`
                            : 'Este proveedor no tiene productos importados'}
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-200 dark:divide-slate-700">
                          {supplierProductsForMappingQuery.data?.products?.map((product: SupplierProductForMapping) => {
                            const isMapped = product.mapped_to === selectedFactusolRef
                            const isMappedToOther = product.mapped_to && product.mapped_to !== selectedFactusolRef

                            return (
                              <div
                                key={product.id}
                                className={`p-3 ${
                                  isMapped
                                    ? 'bg-success-50 dark:bg-success-900/20'
                                    : isMappedToOther
                                    ? 'bg-yellow-50 dark:bg-yellow-900/20 opacity-60'
                                    : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-sm font-medium text-slate-900 dark:text-white">
                                        {product.supplier_reference}
                                      </span>
                                      {product.ean && (
                                        <span className="text-xs text-slate-400">
                                          EAN: {product.ean}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-sm text-slate-600 dark:text-slate-400 truncate">
                                      {product.name}
                                    </div>
                                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                      <span>Coste: {formatPrice(product.cost_price)}</span>
                                      <span>Stock: {product.stock}</span>
                                      {isMappedToOther && (
                                        <span className="text-yellow-600">
                                          Ya mapeado a: {product.mapped_to}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {!isMapped && !isMappedToOther && (
                                    <button
                                      onClick={() => {
                                        createMappingMutation.mutate({
                                          factusol_reference: selectedFactusolRef,
                                          supplier_id: selectedSupplier.id,
                                          supplier_product_id: product.id,
                                        })
                                      }}
                                      disabled={createMappingMutation.isPending}
                                      className="btn btn-primary btn-sm"
                                      title="Crear mapeo"
                                    >
                                      <Link className="w-4 h-4" />
                                    </button>
                                  )}
                                  {isMapped && (
                                    <span className="px-2 py-1 bg-success-100 dark:bg-green-900/50 text-success-700 dark:text-green-300 text-xs rounded flex items-center gap-1">
                                      <CheckCircle className="w-3 h-3" />
                                      Vinculado
                                    </span>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Footer con estadísticas */}
            <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-between">
              <div className="flex gap-4 text-sm">
                {supplierMappingsQuery.data?.stats && (
                  <>
                    <span className="text-slate-500">
                      Total mapeos: <span className="font-medium text-slate-900 dark:text-white">{supplierMappingsQuery.data.stats.total_mappings}</span>
                    </span>
                    <span className="text-slate-500">
                      Productos mapeados: <span className="font-medium text-success-600">{supplierMappingsQuery.data.stats.mapped_products}</span>
                    </span>
                  </>
                )}
              </div>
              <button
                onClick={() => setShowMappingModal(false)}
                className="btn btn-secondary"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para despublicar */}
      {unpublishConfirm.show && selectedSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-warning-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Confirmar desmarcado
                </h3>
              </div>
              <p className="text-slate-600 dark:text-slate-300 mb-2">
                ¿Deseas desmarcar el producto <strong>"{unpublishConfirm.productName}"</strong> como publicado en{' '}
                <strong>{unpublishConfirm.target === 'prestashop' ? 'PrestaShop' : 'Factusol'}</strong>?
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 p-3 rounded-lg">
                <strong>Nota:</strong> Esto solo actualiza el estado en la base de datos local.
                El producto NO se eliminará de {unpublishConfirm.target === 'prestashop' ? 'PrestaShop' : 'Factusol'}.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 dark:bg-slate-900 rounded-b-lg">
              <button
                onClick={() => setUnpublishConfirm({ show: false, productId: null, productName: '', target: null })}
                className="btn btn-secondary"
                disabled={unpublishMutation.isPending}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (unpublishConfirm.productId && unpublishConfirm.target) {
                    unpublishMutation.mutate({
                      supplierId: selectedSupplier.id,
                      productId: unpublishConfirm.productId,
                      target: unpublishConfirm.target
                    })
                  }
                }}
                disabled={unpublishMutation.isPending}
                className="btn btn-primary bg-amber-600 hover:bg-amber-700"
              >
                {unpublishMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Desmarcando...
                  </>
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating action button para creación batch */}
      {selectedProductIds.size > 0 && selectedSupplier && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setShowBatchModal(true)}
            className="flex items-center gap-2 px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-full shadow-lg transition-all hover:shadow-xl"
          >
            <Sparkles className="w-5 h-5" />
            <span className="font-medium">Crear {selectedProductIds.size} productos con IA</span>
          </button>
        </div>
      )}

      {/* Modal de creación batch */}
      <BatchCreateModal
        isOpen={showBatchModal}
        onClose={() => {
          setShowBatchModal(false)
          setSelectedProductIds(new Set())
        }}
        selectedProducts={
          productsQuery.data?.products
            .filter((p) => selectedProductIds.has(p.id))
            .map((p) => ({
              id: p.id,
              name: p.name,
              supplier_reference: p.supplier_reference,
              cost_price: p.cost_price
            })) || []
        }
        supplierCode={selectedSupplier?.code}
      />

    </div>
  )
}
